import React, {useEffect, useState} from "react";
import {Link as RouterLink, useNavigate} from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Link,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import {sendPasswordResetEmail} from "firebase/auth";
import {auth} from "../../firebase";
import {useUserAuth} from "../../context/UserAuthContext";

const SUPPORT_EMAIL = "esf@centro.oep.pt";
const PRIMARY = "#3E4F88";

/**
 * Login genérico para toda a gente (associações, admins, engenheiros).
 *
 * Fluxo igual ao das apps:
 *   1. Login Firebase (email + password)
 *   2. Email verificado?  -> senão, vista de "verificação necessária" + reenviar
 *   3. Papel do utilizador:
 *        admin        -> /dashboard
 *        association  -> /app/associacao  (se ativa) | "à espera de ativação" (se desativada)
 *        engineer     -> /app/engenheiro  (se ativo) | "à espera de ativação" (se desativado)
 *        nenhum       -> aviso + contacto
 */
const SignInPage = () => {

    const {user, role, profile, roleLoading, authLoading, logIn, logOut, verifyEmail} = useUserAuth();
    const navigate = useNavigate();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [info, setInfo] = useState("");
    const [loading, setLoading] = useState(false);
    const [resendSent, setResendSent] = useState(false);

    // Após um login com sucesso, o estado autenticado muda; a vista de
    // "porta" (abaixo) trata do redirecionamento adequado ao papel.
    const gateOpen = !!user;

    // "Sair" nos cartões de estado: termina a sessão e volta à página inicial
    // (a homepage tem os botões Entrar/Registar).
    const signOutHome = () => {
        logOut();
        navigate("/");
    };

    const friendlyError = (err) => {
        switch (err.code) {
            case "auth/invalid-email":
                return "O email indicado não é válido.";
            case "auth/invalid-credential":
            case "auth/wrong-password":
            case "auth/invalid-login-credentials":
                return "Email/palavra-passe errados";
            case "auth/user-not-found":
                return "Não existe uma conta com este email";
            case "auth/missing-password":
                return "Palavra-passe vazia";
            case "auth/too-many-requests":
                return "Muitas tentativas. Tenta mais tarde.";
            default:
                return err.message || "Erro ao entrar. Tenta novamente.";
        }
    };

    const signIn = async (e) => {
        e.preventDefault();
        setError("");
        setInfo("");
        if (!email.trim()) {
            setError("Indica o teu email.");
            return;
        }
        if (!password) {
            setError("Indica a tua palavra-passe.");
            return;
        }
        setLoading(true);
        try {
            await logIn(email.trim(), password);
            // O estado "user" muda via onAuthStateChanged; a porta trata do resto.
        } catch (err) {
            setError(friendlyError(err));
        } finally {
            setLoading(false);
        }
    };

    const resendVerification = async () => {
        setError("");
        setInfo("");
        try {
            await verifyEmail();
            setResendSent(true);
        } catch (err) {
            setError("Não foi possível reenviar o email de verificação. Tenta novamente.");
        }
    };

    const forgotPassword = async () => {
        setError("");
        setInfo("");
        if (!email.trim()) {
            setError("Indica primeiro o teu email e clica em \"Esqueci-me da palavra-passe\".");
            return;
        }
        try {
            await sendPasswordResetEmail(auth, email.trim());
            setInfo(`Enviamos um email para ${email.trim()} para redefinires a palavra-passe.`);
        } catch (err) {
            setError("Não foi possível enviar o email de recuperação.");
        }
    };

    // Redireciona conforme o papel (admin -> /dashboard, associação ativa -> /app)
    // Nota: o admin NÃO passa pela verificação de email — a conta de admin é
    // identificada só pelo email e nunca foi criada pelo fluxo de registo.
    useEffect(() => {
        if (!user || roleLoading) return;
        if (role === "admin") {
            navigate("/dashboard", {replace: true});
        } else if (user.emailVerified === false) {
            return; // a vista de "verificação necessária" aparece abaixo
        } else if (role === "association" && profile && profile.Enabled) {
            navigate("/app/associacao", {replace: true});
        } else if (role === "engineer" && profile && profile.Enabled) {
            navigate("/app/engenheiro", {replace: true});
        }
    }, [user, role, roleLoading, profile, navigate]);

    // ---------- vista "porta" (já autenticado) ----------

    if (authLoading) {
        return (
            <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh"}}>
                <CircularProgress />
            </Box>
        );
    }

    if (gateOpen) {
        // Ainda a descobrir o papel do utilizador
        if (roleLoading) {
            return (
                <GateCard title="A verificar a tua conta…" icon="⏳">
                    <CircularProgress />
                </GateCard>
            );
        }

        // 0) Admin vai sempre para o dashboard (mesmo com email não verificado)
        if (role === "admin") {
            return (
                <GateCard title="A entrar no dashboard…" icon="⏳">
                    <CircularProgress />
                </GateCard>
            );
        }

        // 1) Email ainda não verificado (mesma regra das apps)
        if (user.emailVerified === false) {
            return (
                <GateCard
                    title="Verificação necessária"
                    icon="📧"
                >
                    <Typography paragraph>
                        A tua conta ainda não foi verificada. Por favor, consulta o teu email
                        (e a pasta de spam) e clica no link para confirmar o teu registo.
                    </Typography>
                    {info && <Alert severity="success">{info}</Alert>}
                    {error && <Alert severity="error">{error}</Alert>}
                    <Button
                        variant="contained"
                        sx={{bgcolor: PRIMARY}}
                        onClick={resendVerification}
                    >
                        Reenviar email de verificação
                    </Button>
                    {resendSent && (
                        <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                            Email de verificação reenviado para {user.email}.
                        </Typography>
                    )}
                    <Button onClick={signOutHome} sx={{mt: 1, color: "text.secondary"}}>
                        Sair
                    </Button>
                </GateCard>
            );
        }

        // 2) Associação ainda não ativada por um admin (mesma regra das apps)
        if (role === "association" && profile && profile.Enabled === false) {
            return (
                <GateCard
                    title="Conta à espera de ativação"
                    icon="⏳"
                >
                    <Typography paragraph>
                        A tua conta não está ativada. A tua conta será ativada o mais brevemente possível.
                    </Typography>
                    <Typography paragraph>
                        Em caso de dúvidas, envia um email a{" "}
                        <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>.
                    </Typography>
                    <Button variant="outlined" onClick={signOutHome} sx={{color: PRIMARY, borderColor: PRIMARY}}>
                        Sair
                    </Button>
                </GateCard>
            );
        }

        // 3) Engenheiro ainda não ativado por um admin (mesma regra das apps)
        if (role === "engineer" && profile && profile.Enabled === false) {
            return (
                <GateCard
                    title="Conta à espera de ativação"
                    icon="⏳"
                >
                    <Typography paragraph>
                        A tua conta não está ativada. A tua conta será ativada o mais brevemente possível.
                    </Typography>
                    <Typography paragraph>
                        Em caso de dúvidas, envia um email a{" "}
                        <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>.
                    </Typography>
                    <Button variant="outlined" onClick={signOutHome} sx={{color: PRIMARY, borderColor: PRIMARY}}>
                        Sair
                    </Button>
                </GateCard>
            );
        }

        // 4) Login ok mas sem registo de associação/engenheiro
        if (role === "none") {
            return (
                <GateCard
                    title="Conta sem registo na plataforma"
                    icon="❓"
                >
                    <Typography paragraph>
                        Encontrámos uma conta de autenticação para {user.email},
                        mas sem registo de associação ou engenheiro na plataforma.
                    </Typography>
                    <Typography paragraph>
                        Se precisas de ajuda, envia um email a{" "}
                        <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>.
                    </Typography>
                    <Button variant="outlined" onClick={signOutHome} sx={{color: PRIMARY, borderColor: PRIMARY}}>
                        Sair
                    </Button>
                </GateCard>
            );
        }

        // Caso residual (ex.: admin com email não verificado já tratado acima)
        return (
            <GateCard title="A preparar o redirecionamento…" icon="⏳">
                <CircularProgress />
            </GateCard>
        );
    }

    // ---------- vista de login ----------

    return (
        <Box
            component="main"
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                bgcolor: "#f4f5fa",
                py: 4,
            }}
        >
            <Container maxWidth="xs">
                <Box
                    sx={{
                        bgcolor: "background.paper",
                        borderRadius: 2,
                        border: 1,
                        borderColor: "divider",
                        p: 4,
                    }}
                >
                    <Stack spacing={3} alignItems="stretch">
                        <Button
                            size="small"
                            component={RouterLink}
                            to="/"
                            sx={{textTransform: "none", alignSelf: "flex-start", color: "text.secondary"}}
                        >
                            ← Voltar à página inicial
                        </Button>
                        <Stack spacing={0.5} alignItems="center">
                            <Typography variant="h5" sx={{fontWeight: 700, color: PRIMARY, textAlign: "center"}}>
                                Entrar
                            </Typography>
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                Associações, engenheiros e administradores da plataforma E4IU
                            </Typography>
                        </Stack>

                        {error && <Alert severity="error">{error}</Alert>}
                        {info && <Alert severity="success">{info}</Alert>}

                        <form onSubmit={signIn} noValidate>
                            <Stack spacing={2}>
                                <TextField
                                    label="Email"
                                    type="email"
                                    size="small"
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                                <TextField
                                    label="Palavra-passe"
                                    type="password"
                                    size="small"
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={loading}
                                    sx={{bgcolor: PRIMARY, py: 1.2, fontWeight: 600}}
                                >
                                    {loading ? "A entrar…" : "Entrar"}
                                </Button>
                            </Stack>
                        </form>

                        <Stack spacing={1} sx={{mt: 1}}>
                            <Button size="small" onClick={forgotPassword} sx={{textTransform: "none"}}>
                                Esqueci-me da palavra-passe
                            </Button>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    size="small"
                                    component={RouterLink}
                                    to="/registo/associacao"
                                    sx={{textTransform: "none", color: PRIMARY, fontWeight: 600}}
                                >
                                    Registar associação
                                </Button>
                                <Button
                                    size="small"
                                    component={RouterLink}
                                    to="/registo/engenheiro"
                                    sx={{textTransform: "none", color: PRIMARY, fontWeight: 600}}
                                >
                                    Registar voluntário/a
                                </Button>
                            </Stack>
                        </Stack>
                    </Stack>
                </Box>
            </Container>
        </Box>
    );
};

/** Cartão simples para as vistas de estado da conta. */
const GateCard = ({title, icon, children}) => (
    <Box
        sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            bgcolor: "#f4f5fa",
            py: 4,
        }}
    >
        <Container maxWidth="xs">
            <Box
                sx={{
                    bgcolor: "background.paper",
                    borderRadius: 2,
                    border: 1,
                    borderColor: "divider",
                    p: 4,
                }}
            >
                <Stack spacing={2} alignItems="stretch">
                    <Typography variant="h5" sx={{fontWeight: 700, color: "#3E4F88", textAlign: "center"}}>
                        {icon} {title}
                    </Typography>
                    {children}
                </Stack>
            </Box>
        </Container>
    </Box>
);

export default SignInPage;